import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Store, Zap, Shield, Bolt, Sparkles } from 'lucide-react'
import { storeService } from '../services/storeService'
import useToastStore from '../store/useToastStore'

export const OnboardingPage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [storeName, setStoreName] = useState('');
  const [storeUrl, setStoreUrl] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const toast = useToastStore();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!storeName.trim() || !storeUrl.trim()) {
      toast.showToast('Please fill in all fields', 'error');
      return;
    }

    try {
      setIsLoading(true);
      await storeService.createStore({ brandName: storeName, url: storeUrl });
      setIsSuccess(true);
      toast.showToast('Store created successfully!', 'success');
    } catch (error) {
      console.error('Error creating store:', error);
      toast.showToast('Failed to create store. Please try again.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 flex items-center justify-center p-4 relative overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-24 md:w-32 h-24 md:h-32 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse"></div>
          <div className="absolute top-40 right-20 w-16 md:w-24 h-16 md:h-24 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse animation-delay-1000"></div>
          <div className="absolute bottom-20 left-20 w-24 md:w-40 h-24 md:h-40 bg-pink-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse animation-delay-2000"></div>
        </div>

        <div className="w-full max-w-md md:max-w-2xl text-center relative z-10 px-4">
          {/* Celebration Animation */}
          <div className="relative mb-6 md:mb-8">
            <div className="w-20 h-20 md:w-32 md:h-32 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full flex items-center justify-center mx-auto shadow-[0_0_50px_rgba(99,102,241,0.3)] animate-bounce-slow">
              <Sparkles className="w-10 h-10 md:w-16 md:h-16 text-white animate-pulse" />
            </div>
            <div className="absolute -top-2 -right-2 md:-top-4 md:-right-4 w-4 h-4 md:w-8 md:h-8 bg-yellow-400 rounded-full animate-bounce animation-delay-100"></div>
            <div className="absolute -bottom-2 -left-2 md:-bottom-4 md:-left-4 w-4 h-4 md:w-8 md:h-8 bg-pink-400 rounded-full animate-bounce animation-delay-200"></div>
            <div className="absolute -top-2 -left-2 md:-top-4 md:-left-4 w-4 h-4 md:w-8 md:h-8 bg-green-400 rounded-full animate-bounce animation-delay-300"></div>
          </div>

          <h1 className="text-3xl md:text-6xl font-bold mb-4 md:mb-6 bg-gradient-to-r from-purple-600 via-blue-600 to-purple-600 inline-block text-transparent bg-clip-text animate-gradient">
            Congratulations! 🎉
          </h1>
          <p className="text-gray-600 mb-6 md:mb-8 text-lg md:text-2xl">
            Your store has been created successfully
          </p>

          <div className="bg-white/80 backdrop-blur-sm rounded-xl md:rounded-2xl p-4 md:p-8 mb-6 md:mb-8 border border-purple-100 shadow-xl transform hover:scale-[1.02] transition-all duration-300">
            <div className="space-y-3 md:space-y-4">
              <div className="flex items-center justify-center gap-2 md:gap-3 p-3 md:p-4 bg-purple-50 rounded-lg md:rounded-xl">
                <Store className="w-5 h-5 md:w-6 md:h-6 text-purple-600" />
                <div className="text-left">
                  <p className="text-xs md:text-sm text-gray-500">Store Name</p>
                  <p className="text-base md:text-lg font-semibold text-purple-600">{storeName}</p>
                </div>
              </div>
              <div className="flex items-center justify-center gap-2 md:gap-3 p-3 md:p-4 bg-blue-50 rounded-lg md:rounded-xl">
                <svg className="w-5 h-5 md:w-6 md:h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
                <div className="text-left">
                  <p className="text-xs md:text-sm text-gray-500">Store URL</p>
                  <p className="text-base md:text-lg font-semibold text-blue-600">{storeUrl}.mart-24.com</p>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-3 md:space-y-4">
            <a
              href={`https://${storeUrl}.mart-24.com`}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg md:rounded-xl px-4 md:px-6 py-3 md:py-4 font-medium hover:shadow-lg transition-all duration-300 transform hover:scale-105 group"
            >
              <div className="flex items-center justify-center gap-2 md:gap-3">
                <span className="text-sm md:text-base">Visit Your Store</span>
                <svg className="w-4 h-4 md:w-5 md:h-5 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </div>
            </a>
            <button
              onClick={() => navigate('/admin')}
              className="w-full bg-white text-gray-700 rounded-lg md:rounded-xl px-4 md:px-6 py-3 md:py-4 font-medium border border-gray-200 hover:border-purple-200 hover:shadow-lg transition-all duration-300 transform hover:scale-105 group"
            >
              <div className="flex items-center justify-center gap-2 md:gap-3">
                <span className="text-sm md:text-base">Go to Admin Dashboard</span>
                <svg className="w-4 h-4 md:w-5 md:h-5 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </button>
          </div>
        </div>

        <style>{`
          @keyframes gradient {
            0%, 100% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
          }
          .animate-gradient {
            background-size: 200% 200%;
            animation: gradient 3s ease infinite;
          }
          .animation-delay-100 { animation-delay: 0.1s; }
          .animation-delay-200 { animation-delay: 0.2s; }
          .animation-delay-300 { animation-delay: 0.3s; }
          .animation-delay-1000 { animation-delay: 1s; }
          .animation-delay-2000 { animation-delay: 2s; }
          .animate-bounce-slow {
            animation: bounce 2s infinite;
          }
          @keyframes bounce {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-10px); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-32 h-32 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse"></div>
        <div className="absolute top-40 right-20 w-24 h-24 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse animation-delay-1000"></div>
        <div className="absolute bottom-20 left-20 w-40 h-40 bg-pink-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse animation-delay-2000"></div>
      </div>

      <div className="max-w-xl w-full relative z-10">
        <div className="text-center mb-12">
          <div className="w-24 h-24 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-8 shadow-[0_0_30px_rgba(99,102,241,0.3)]">
            <Store className="w-12 h-12 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-purple-600 to-blue-600 inline-block text-transparent bg-clip-text">
            Create Your Store
          </h1>
          <p className="text-gray-600 text-lg md:text-xl">Launch your digital storefront in minutes</p>
        </div>

        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-purple-100 shadow-xl">
          <form onSubmit={handleSubmit} className="space-y-6">
          <div>
              <label htmlFor="storeName" className="block text-sm font-medium text-gray-700 mb-2">
                Store Name
            </label>
            <input
              type="text"
                id="storeName"
                value={storeName}
                onChange={(e) => setStoreName(e.target.value)}
                placeholder="Enter your store name"
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-purple-200 focus:ring-2 focus:ring-purple-100 transition-all duration-300"
                disabled={isLoading}
              />
          </div>

          <div>
              <label htmlFor="storeUrl" className="block text-sm font-medium text-gray-700 mb-2">
                Store URL
            </label>
            <div className="relative">
              <input
                type="text"
                  id="storeUrl"
                  value={storeUrl}
                  onChange={(e) => setStoreUrl(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                placeholder="your-store"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-purple-200 focus:ring-2 focus:ring-purple-100 transition-all duration-300 pr-32"
                  disabled={isLoading}
              />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">
                .mart-24.com
              </span>
            </div>
          </div>

          <button
            type="submit"
              disabled={isLoading}
              className={`w-full flex items-center justify-center gap-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 px-4 rounded-xl hover:shadow-lg transition-all duration-300 transform hover:scale-105 ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : null}
              <span className="font-medium">
                {isLoading ? 'Creating Store...' : 'Create Store'}
              </span>
          </button>
        </form>
      </div>
      </div>

      <style>{`
        @keyframes gradient {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient 3s ease infinite;
        }
        .animation-delay-1000 { animation-delay: 1s; }
        .animation-delay-2000 { animation-delay: 2s; }
      `}</style>
    </div>
  );
}